import React, { useState, useEffect } from "react";
import styles from "../../styles/styles";
import { Country, State } from "country-state-city";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { server } from "../../server";
import { toast } from "react-toastify";
import { updateUserAddress } from "../../redux/actions/user"; // Corrected import for address action
import { logEvent } from "../../analytics/ga4";

const Checkout = () => {
  const { user } = useSelector((state) => state.user);
  const { cart } = useSelector((state) => state.cart);
  const [addressName, setAddressName] = useState("");
  const [country, setCountry] = useState("IN");
  const [city, setCity] = useState("TN");
  const [userInfo, setUserInfo] = useState(false);
  const [address1, setAddress1] = useState("");
  const [zipCode, setZipCode] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponCodeData, setCouponCodeData] = useState(null);
  const [discountPrice, setDiscountPrice] = useState(0);
  const [popupOpen, setPopupOpen] = useState(false); // Popup state for adding address
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    sendBeginCheckoutEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prepareEcommerceItems = (cart) => {
    const items = cart.map((item) => {
      return {
        item_id: item._id,
        item_name: item.name,
        item_category: item.category,
        price: parseFloat(item.totalPriceWithCommission),
        quantity: item.qty,
      };
    });
    return items;
  };

  // <-- Function to send `begin_checkout` event to GA4
  const sendBeginCheckoutEvent = () => {
    if (cart.length === 0) return; // Avoid sending event if cart is empty

    const eventData = {
      currency: "INR",
      value: parseFloat(ordertotalPriceWithCommission),
      items: prepareEcommerceItems(cart),
      shipping_cost: parseFloat(shipping),
    };
    logEvent("begin_checkout", eventData);
  };

  const calculateShopTotalPrices = (cart) => {
    const shopTotals = {};

    cart.forEach((item) => {
      if (!shopTotals[item.shop]) {
        shopTotals[item.shop] = {
          shopTotalPriceWithCommission: 0,
          shopTotalPriceWithoutCommission: 0,
        };
      }

      shopTotals[item.shop].shopTotalPriceWithCommission +=
        item.qty * item.totalPriceWithCommission;
      shopTotals[item.shop].shopTotalPriceWithoutCommission +=
        item.qty * item.totalPriceWithoutCommission;
    });

    return shopTotals;
  };

  const paymentSubmit = () => {
    if (
      address1 === "" ||
      zipCode === null ||
      country === "" ||
      city === ""
    ) {
      toast.error("Please choose your delivery address!");
    } else {
      const shippingAddress = {
        address1,
        zipCode,
        country,
        city,
      };

      const shopTotals = calculateShopTotalPrices(cart);

      const orderData = {
        cart,
        orderTotalPriceWithCommission: ordertotalPriceWithCommission,
        orderTotalPriceWithoutCommission: ordertotalPriceWithoutCommission,
        subTotalPriceWithCommission: subTotalPriceWithCommission,
        subTotalPriceWithoutCommission: subTotalPriceWithoutCommission,
        shipping: parseFloat(shipping), 
        discountPrice,
        shippingAddress,
        user: user._id,
        shopTotals, // Include shop totals
      };

      localStorage.setItem("latestOrder", JSON.stringify(orderData));
      navigate("/payment");
    }
  };

  const subTotalPriceWithCommission = cart.reduce(
    (acc, item) => acc + item.qty * item.totalPriceWithCommission,
    0
  );
  const subTotalPriceWithoutCommission = cart.reduce(
    (acc, item) => acc + item.qty * item.totalPriceWithoutCommission,
    0
  );

  // shipping cost: free only in TN above 3000, 15% for others, 100 base
  let shipping;
  const isTamilNadu = city === "TN";

  if (isTamilNadu && subTotalPriceWithCommission > 3000) {
    shipping = 0;
  } else if (subTotalPriceWithCommission > 1000) {
    shipping = subTotalPriceWithCommission * 0.15;
  } else {
    shipping = 100;
  }

  const discountPercentenge = couponCodeData ? discountPrice : 0; // Set default value to 0 instead of an empty string

  const ordertotalPriceWithCommission = couponCodeData
    ? (subTotalPriceWithCommission + shipping - discountPercentenge).toFixed(2)
    : (subTotalPriceWithCommission + shipping).toFixed(2);

  const ordertotalPriceWithoutCommission = couponCodeData
    ? (
        subTotalPriceWithoutCommission +
        shipping -
        discountPercentenge
      ).toFixed(2)
    : (subTotalPriceWithoutCommission + shipping).toFixed(2);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = couponCode;

    await axios
      .get(`${server}/coupon/get-coupon-value/${name}`)
      .then((res) => {
        const shop = res.data.couponCode?.shop;
        const couponCodeValue = res.data.couponCode?.value;
        if (res.data.couponCode !== null) {
          const isCouponValid =
            cart && cart.filter((item) => item.shop === shop);

          if (isCouponValid.length === 0) {
            toast.error("Coupon code is not valid for this shop");
            setCouponCode("");
          } else {
            const eligiblePrice = isCouponValid.reduce(
              (acc, item) =>
                acc + item.qty * item.totalPriceWithCommission,
              0
            );
            const discountPrice = (eligiblePrice * couponCodeValue) / 100;
            setDiscountPrice(discountPrice);
            setCouponCodeData(res.data.couponCode);
            setCouponCode("");
          }
        }
        if (res.data.couponCode === null) {
          toast.error("Coupon code doesn't exists!");
          setCouponCode("");
        }
      });
  };

  const handleAddressSubmit = (e) => {
    e.preventDefault();
    if (
      addressName === "" ||
      address1 === "" ||
      zipCode === "" ||
      country === "" ||
      city === ""
    ) {
      toast.error("Please fill all fields!");
    } else {
      dispatch(
        updateUserAddress(
          country,
          city,
          address1,
          zipCode,
          addressName
        )
      );
      setPopupOpen(false);
      toast.success("Address saved for fast checkout!");
    }
  };

  return (
    <div className="w-full flex flex-col items-center py-8">
      <div className="w-[90%] 1000px:w-[70%] block 800px:flex">
        <div className="w-full 800px:w-[65%]">
          <ShippingInfo
            user={user}
            country={country}
            setCountry={setCountry}
            city={city}
            setCity={setCity}
            userInfo={userInfo}
            setUserInfo={setUserInfo}
            address1={address1}
            setAddress1={setAddress1}
            zipCode={zipCode}
            setZipCode={setZipCode}
            setPopupOpen={setPopupOpen} // Pass setPopupOpen as a prop
          />
          {/* Removed the original "Save new address" button */}
        </div>

        <div className="w-full 800px:w-[35%] 800px:mt-0 mt-8">
          <CartData
            handleSubmit={handleSubmit}
            ordertotalPriceWithCommission={ordertotalPriceWithCommission}
            ordertotalPriceWithoutCommission={ordertotalPriceWithoutCommission}
            shipping={shipping}
            subTotalPriceWithCommission={subTotalPriceWithCommission}
            subTotalPriceWithoutCommission={subTotalPriceWithoutCommission}
            couponCode={couponCode}
            setCouponCode={setCouponCode}
            discountPercentenge={discountPercentenge}
            city={city}
          />
        </div>
      </div>
      <div
        className={`${styles.button} w-[150px] 800px:w-[280px] mt-10`}
        onClick={paymentSubmit}
      >
        <h5 className="text-white">Go to Payment</h5>
      </div>
      {popupOpen && (
        <div className="fixed w-full h-screen bg-[#0000004b] top-0 left-0 flex items-center justify-center">
          <div className="w-[95%] sm:w-[35%] bg-white p-8 rounded shadow-lg">
            <h2 className="text-xl font-semibold mb-4">Add New Address</h2>
            <form onSubmit={handleAddressSubmit}>
              <div className="mb-4">
                <label className="block mb-1">Address Name</label>
                <input
                  type="text"
                  className={`${styles.input}`}
                  value={addressName}
                  onChange={(e) => setAddressName(e.target.value)}
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block mb-1">Country</label>
                <select
                  className="w-full border h-[40px] rounded-md"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                >
                  <option value="">Choose your country</option>
                  {Country.getAllCountries().map((item) => (
                    <option key={item.isoCode} value={item.isoCode}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block mb-1">State</label>
                <select
                  className="w-full border h-[40px] rounded-md"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                >
                  <option value="">Choose your state</option>
                  {State.getStatesOfCountry(country).map((item) => (
                    <option key={item.isoCode} value={item.isoCode}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block mb-1">Address 1</label>
                <input
                  type="text"
                  className={`${styles.input}`}
                  value={address1}
                  onChange={(e) => setAddress1(e.target.value)}
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block mb-1">Zip Code</label>
                <input
                  type="text"
                  className={`${styles.input}`}
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  required
                />
              </div>
              <div className="flex justify-between">
                <button
                  type="submit"
                  className={`${styles.button} w-[45%] bg-[#333] text-white`}
                >
                  Save Address
                </button>
                <button
                  type="button" // Changed to type="button" to prevent form submission
                  className={`${styles.button} w-[45%] bg-[#333] text-white`}
                  onClick={() => setPopupOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const ShippingInfo = ({
  user,
  country,
  setCountry,
  city,
  setCity,
  userInfo,
  setUserInfo,
  address1,
  setAddress1,
  zipCode,
  setZipCode,
  setPopupOpen, // Receive setPopupOpen as a prop
}) => {
  const [selectedAddress, setSelectedAddress] = useState("");
  return (
    <div className="w-full bg-white rounded-md p-6 shadow-md">
      <h5 className="text-xl font-semibold mb-6">Shipping Address</h5>
      <form>
        {/* Name and Email */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Full Name */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Company Name / Full Name
            </label>
            <input
              type="text"
              value={user && user.name}
              required
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {/* Email Address */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={user && user.email}
              required
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Phone Number and Zip Code */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {/* Phone Number */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              required
              value={user && user.phoneNumber}
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {/* Zip Code */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Zip Code
            </label>
            <input
              type="text"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              required
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Country and State */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {/* Country */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Country
            </label>
            <select
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            >
              <option value="">Choose your country</option>
              {Country &&
                Country.getAllCountries().map((item) => (
                  <option key={item.isoCode} value={item.isoCode}>
                    {item.name}
                  </option>
                ))}
            </select>
          </div>
          {/* State */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              State / City
            </label>
            <select
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            >
              <option value="">Choose your city</option>
              {State &&
                State.getStatesOfCountry(country).map((item) => (
                  <option key={item.isoCode} value={item.isoCode}>
                    {item.name}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {/* Address1 and Address2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {/* Address1 */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">
              Address Line
            </label>
            <input
              type="text"
              required
              value={address1}
              onChange={(e) => setAddress1(e.target.value)}
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </form>

      {/* Saved Addresses */}
      <div className="mt-8">
        <button
          className="text-blue-600 underline font-medium"
          onClick={() => setUserInfo(!userInfo)}
        >
          Choose from saved addresses
        </button>
        {userInfo && (
          <div className="mt-4">
            {user &&
              user.addresses.map((item, index) => (
                <div
                  className="flex items-center mb-4 p-4 border rounded-md cursor-pointer hover:bg-gray-50"
                  key={index}
                  onClick={() => {
                    setSelectedAddress(item.addressType);
                    setAddress1(item.address1);
                    setZipCode(item.zipCode);
                    setCountry(item.country);
                    setCity(item.city);
                  }}
                >
                  <input
                    type="radio"
                    className="form-radio h-5 w-5 text-blue-600"
                    checked={selectedAddress === item.addressType}
                    onChange={() => setSelectedAddress(item.addressType)}
                  />
                  <div className="ml-4">
                    <h2 className="text-lg font-semibold">
                      {item.addressType}
                    </h2>
                    <p className="text-gray-600">
                      {item.address1}, {item.city},{" "}
                      {item.zipCode}, {item.country}
                    </p>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
       {/* New "Save new address" button */}
       <button
          className="text-blue-600 underline font-medium mt-2"
          onClick={() => setPopupOpen(true)}
        >
          Save new address for future and fast check out
        </button>
    </div>
  );
};

const CartData = ({
  handleSubmit,
  ordertotalPriceWithCommission,
  ordertotalPriceWithoutCommission,
  shipping,
  subTotalPriceWithCommission,
  subTotalPriceWithoutCommission,
  couponCode,
  setCouponCode,
  discountPercentenge,
  city,
}) => {
  const isTamilNadu = city === "TN";

  return (
    <div className="w-full bg-white rounded-lg shadow-md p-6">
      {/* Subtotal */}
      <div className="flex justify-between items-center">
        <h3 className="text-base font-medium text-gray-700">Subtotal:</h3>
        <h5 className="text-lg font-semibold text-gray-800">
          Rs {subTotalPriceWithCommission.toFixed(2)}
        </h5>
      </div>

      {/* Shipping */}
      <div className="flex justify-between items-center mt-4">
        <h3 className="text-base font-medium text-gray-700">Shipping:</h3>
        <h5 className="text-lg font-semibold text-gray-800">
          Rs {shipping.toFixed(2)}
        </h5>
      </div>

      {/* Discount */}
      <div className="flex justify-between items-center mt-4 border-b pb-4">
        <h3 className="text-base font-medium text-gray-700">Discount:</h3>
        <h5 className="text-lg font-semibold text-gray-800">
          - Rs {discountPercentenge ? discountPercentenge.toString() : 0}
        </h5>
      </div>

      {/* Total */}
      <div className="flex justify-between items-center mt-4">
        <h5 className="text-xl font-bold text-gray-900">Total:</h5>
        <h5 className="text-xl font-bold text-gray-900">
          Rs {ordertotalPriceWithCommission}
        </h5>
      </div>

      {/* Shipping Info Message */}
      <div className="mt-6 text-center">
        {isTamilNadu ? (
          <>
            <p className="text-sm text-gray-600">
              Free shipping on orders above Rs 3000 in Tamil Nadu.
            </p>
            {subTotalPriceWithCommission < 3000 && (
              <p className="mt-2 text-sm text-red-600">
                Add items worth Rs{' '}
                {(3000 - subTotalPriceWithCommission).toFixed(2)} to get free delivery.
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-600">
            Shipping charges are applied for your location.
          </p>
        )}
      </div>

      {/* Coupon Code Form */}
      {/* <form onSubmit={handleSubmit} className="mt-6">
        <input
          type="text"
          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Coupon code"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value)}
          required
        />
        <button
          type="submit"
          className="w-full mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-300"
        >
          Apply Code
        </button>
      </form> */}
    </div>
  );
};

export default Checkout;
